import React, { useContext, useState } from "react";
import { Modal } from "@mui/material";
import { Visibility as PreviewIcon } from "@mui/icons-material";

import {
    ButtonRow,
    Expandable,
    InfoText,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../MaterialOrder/Styled";
import { ExportMaterialForm } from "./ExportMaterialForm";
import { ExportMaterialList } from "./ExportMaterialList";
import { api } from "../../../shared/hooks/api";
import { success, error } from "../../../utils/toast";
import useMergeState from "../../../shared/hooks/useMergeState";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { ROLE } from "../../../data/enums";
import { getStorageForRole } from "../../Products/Title/ExportProductModal";
import LoadingButton from "../../../shared/components/LoadingButton";
import Template from "../../../templates/export_material_template.docx";
import { generateDocumentBlob } from "../../../utils/exportUtils";
import DocxPreviewModal from "../../../shared/components/DocxPreviewModal";
import { formatInternationalNumber } from "../../../utils/format";

interface ExportMaterialModalProps {
    open: boolean;
    handleClose: () => void;
    materials: any[];
    handleConfirm?: () => void | Promise<void>;
    contentType?: string;
    objectId?: string;  // Changed from number to string for UUID
}

const getInitialState = (materials: any[], role: ROLE) => {
    return {
        formData: {
            storage: getStorageForRole(role),
            export_method: "",
            department: "",
            receiverer_name: "",
            export_date: "",
            note: "",
        },
        selectedMaterials: (!materials || !Array.isArray(materials) || materials.length === 0) 
            ? [] 
            : materials.filter(material => material.exportQuantity > 0)
    };
};

export const ExportMaterialModal = ({ open, handleClose, materials, handleConfirm, contentType, objectId }: ExportMaterialModalProps) => {
    const {user: {role}} = useContext(AuthenticationContext);

    const [state, mergeState] = useMergeState(getInitialState(materials, role));
    const [{ isCreating }, createExport] = api.post("/materials/exports/");
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

    const mergeFormData = (newFormData: any) => {
        mergeState({ formData: { ...state.formData, ...newFormData } });
    };

    const mergeSelectedMaterials = (newSelectedMaterials: any) => {
        mergeState({ selectedMaterials: newSelectedMaterials });
    };

    const handleCloseWithAnimation = () => {
        setTimeout(handleClose, 200);
    };

    const validateExportData = () => {
        const errors: string[] = [];

        if (!state.formData.storage) {
            errors.push("Kho xuất không được để trống.");
        }

        if (!state.formData.export_method) {
            errors.push("Phương thức xuất không được để trống.");
        }

        if (!state.formData.department) {
            errors.push(contentType ? "Khách hàng không được để trống." : "Đối tượng không được để trống");
        }

        if (!state.formData.export_date || isNaN(new Date(state.formData.export_date).getTime())) {
            errors.push("Ngày xuất kho không hợp lệ.");
        }

        if (!state.selectedMaterials.length) {
            errors.push("Danh sách vật tư không được để trống.");
        }
        state.selectedMaterials.forEach((material: any, index: number) => {
            const availableQuantity =
                material.available_quantity ?? material.quantity ?? 0;
            if (!material.exportQuantity || material.exportQuantity <= 0) {
                errors.push(`Vật tư ${index + 1}: Số lượng xuất phải lớn hơn 0.`);
            }
            if (material.exportQuantity > availableQuantity) {
                errors.push(
                    `Vật tư ${index + 1}: Số lượng xuất không được vượt quá số lượng khả dụng (${formatInternationalNumber(availableQuantity)}).`,
                );
            }
        });

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const handlePreview = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert("Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" + validation.errors.join("\n\n"));
            return;
        }

        const itemDate = new Date(state.formData.export_date);
        const exportData = {
            storage: state.formData.storage || "",
            receiverer_name: state.formData.receiverer_name || "",
            export_method: state.formData.export_method || "",
            department: state.formData.department || "",
            source: state.formData.source || "", // This might be empty for preview
            note: state.formData.note || "",
            day: itemDate.getDate(),
            month: itemDate.getMonth() + 1,
            year: itemDate.getFullYear(),
            export_code: "PHIẾU XEM TRƯỚC", // Preview mode
            materials: state.selectedMaterials.map((material: any, index: number) => ({
                index: index + 1,
                name: material.name || "",
                quantity: material.exportQuantity || "",
                measurement_unit: material.measurement_unit || "",
                code: material.material_code || material.id || "",
            }))
        };

        try {
            const blob = await generateDocumentBlob(exportData, Template);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const handleExport = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert(validation.errors.join("\n\n"));
            return;
        }

        try {
            const exportData = {
                ...state.formData,
                materials: state.selectedMaterials.map((item: any) => ({
                    material: item.id,
                    quantity: item.exportQuantity,
                    note: item.note || "",
                })),
                content_type: contentType,
                object_id: objectId
            };

            await createExport(exportData);
            success("THÀNH CÔNG", "Xuất kho vật tư thành công");

            setIsPreviewOpen(false);
            setPreviewBlob(null);
            await handleConfirm?.();
            handleCloseWithAnimation();
        } catch (err) {
            error("Không thể xuất kho vật tư", err);
        }
    };

    return (
        <>
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={open}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Xuất kho vật tư</TitleText>
                        <InfoText>Vui lòng điền thông tin xuất kho và chọn vật tư</InfoText>
                    </TitleWrapper>

                    <ExportMaterialForm standalone={!contentType} formData={state.formData} onChange={mergeFormData} />

                    <TitleText>Danh sách xuất kho vật tư</TitleText>

                    <ExportMaterialList
                        selectedMaterials={state.selectedMaterials}
                        setSelectedMaterials={mergeSelectedMaterials}
                    />
                </Expandable>

                <ButtonRow>
                    <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                    <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">
                        Xem trước phiếu
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
        <DocxPreviewModal
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            blob={previewBlob}
            title="Xem trước phiếu xuất kho"
            BackdropComponent={StyledBackdrop}
            backdropProps={{ timeout: 300 }}
            debug
            actions={
                <LoadingButton
                    variant="contained"
                    onClick={handleExport}
                    disabled={isCreating}
                    isLoading={isCreating}
                    text="Xuất kho"
                    style={undefined}
                />
            }
        />
        </>
    );
};
